<template>
	<model-viewer
		:src="$store.state.activeModel"
		environment-image="neutral"
		camera-orbit="-50deg 75deg 65%"
		min-camera-orbit="auto auto 80%"
		max-camera-orbit="auto 95deg 65%"
		animation-crossfade-duration="500"
		animation-control-mode="manual"
		interaction-prompt-style="basic"
		:interaction-prompt="modelPrompt"
		camera-controls
		:class="{ 'hotspots-visible': activePrimaryNavigation === 'keyFeatures' && activeSecondaryNavigation }"
		@progress="updateProgress"
		@load="onModelLoad"
		@camera-change="onCameraChange"
		v-html="$store.state.productConfig.hotspots"
	/>

  <!-- camera-orbit="-50deg 75deg 65%" -->

	<!-- <div
		v-if="$store.state.debug"
		class="debug-info"
	>
		Orbit: {{ cameraOrbit }}<br>
		Target: {{ cameraTarget }}
	</div> -->
</template>

<script>
import { mapState } from 'vuex'
import 'model-viewer'
// import '/Users/Roman/Work/Brainz/repos/model-viewer/dist/model-viewer.js'

export default {
	name: 'ModelView',
	props: {
		playing: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			modelPrompt: 'auto',
			modelPlaying: false,
			modelTime: 0,
			modelViewer: null,
			modelActions: {},
			hotspots: [],
			animationsInProgress: 0,

			cameraOrbit: '',
			cameraTarget: ''
		}
	},
	computed: mapState({
		activePrimaryNavigation: state => state.navigation.activePrimary,
		activeSecondaryNavigation: state => state.navigation.activeSecondary,
		activeSubExplanation: state => state.explanationPaneSub,
		explanationPaneOpen: state => state.explanationPaneOpen,
		animationsList: state => state.productConfig.animations,
		modelLoadingProgress: state => state.modelLoadingProgress,
		productConfig: state => state.productConfig,
    transferModeOn: state => state.navigation.transferModeOn,
    siderailsCheck: state => state.navigation.siderailsCheck
	}),
	mounted() { 
		this.$bus.on('requestModelAnimation', ({ name, direction, binary }) => {
			const binaryAnimationActive = binary && this.modelActions[name]?.time > 0      

      // leg check
      if (name === 'calfrest_adjustment' || name === 'thighrest_adjustment') {
        if (name === 'calfrest_adjustment' && this.modelActions['thighrest_adjustment']?.time === 0) return
        this.handleLegAdjustment(name, direction)
        return
      }

      // frame lateral tilt
      if (name === 'frame_based_lateral_tilt_left' || name === 'frame_based_lateral_tilt_right') {
        this.handleLateralTilting(name)
        return
      }

      // foot siderails
      if (name === 'folding_of_foot_siderails') {
        this.handleFootSiderails(direction)
        return
      }

      // head siderails
      if (name === 'folding_of_head_siderails') {
        this.handleHeadSiderails(direction)
        return
      }

      // backrest
      if (name === 'backrest_adjustment') {
        this.handleBackrestAdjustment(direction)
        return
      }

      // cardiac chair
      if (name === 'cardiac_chair') {
        if (this.animationsInProgress > 0) return
        this.handleCardiacChair()
        return
      }

			this.handleAnimationRequest(name, binaryAnimationActive || direction === 'down')
		})

		this.$bus.on('requestModelAnimationsReset', () => {
			this.resetAnimations(Object.keys(this.animationsList))
		})

		this.$bus.on('requestModelPartsVisibility', nodes => {
			nodes.forEach(node => {this.setModelNodeVisibility(node.name, node.visible)})
		})

		this.$bus.on('requestCameraPosition', cameraPosition => {
			this.setCameraPosition(cameraPosition)
		})

		window.modelVM = this // TODO: for debugging only, remove when going into production
	},
	watch: {
		activeSecondaryNavigation(val) {
			if (this.activePrimaryNavigation === 'keyFeatures') {
				// disable hotspots not related to active feature
				this.hotspots.forEach(hotspot => {
					const navItemName = val.split('.')[1]
					const hotspotExplanations = hotspot.dataset.explanations?.split('.')
					hotspot.classList.remove('active')
					if (hotspotExplanations[0] === navItemName) {
						if (hotspotExplanations.length === 1) {
							hotspot.classList.add('active')
						}
						hotspot.removeAttribute('disabled')
					} else {
						hotspot.setAttribute('disabled', '')
					}
				})
			}
		},
		activeSubExplanation(name) {
			if (this.activePrimaryNavigation === 'keyFeatures') {
				// display hotspot as active when related feature + subExplanation is selected
				this.hotspots.forEach(hotspot => {
					const hotspotExplanation = hotspot.dataset.explanations?.split('.')[1]
					if (hotspotExplanation === name) {
						hotspot.classList.add('active')
					} else {
						hotspot.classList.remove('active')
					}
				})
			}
		},
		explanationPaneOpen(open) {
			if (!open) {
				this.$nextTick(() => {
					this.resetCameraPosition()
				})
			}
		}
	},
	methods: {
		onModelLoad(e) {
			const viewer = e.target
			const actions = {}

			// add all available animations to mixer and set reference for easy access
			for (const [name, animation] of Object.entries(this.animationsList)) {
				const animationTrack = viewer.scene.animationsByName.get(animation.track)

				if (animationTrack) {
					const action = viewer.animationMixer.clipAction(animationTrack)

					action.setLoop(viewer.availableLoopModes.once)
					action.clampWhenFinished = true

					actions[name] = action
				}
			}

			// set up hotspots (positioning is done by modelviewer)
			viewer.querySelectorAll('button').forEach(hotspot => {
				hotspot.setAttribute('disabled', '')
				hotspot.addEventListener('click', () => this.onHotspotClick(hotspot))
				this.hotspots.push(hotspot)
			})

			this.modelActions = actions
			this.modelViewer = viewer
			window.modelViewer = viewer
      window.modelActions = actions      
		},

    async handleCardiacChair() {
      const bhdTiming = this.modelActions['bed_height_adjustment_down']?.time > 0
      const trendelenburgTiming = this.modelActions['trendelenburg']?.time > 0
      const reverseTrendelenburgTiming = this.modelActions['reverse_trendelenburg']?.time > 0

      const br30Timing = this.modelActions['backrest_adjustment30']?.time > 0
      const br45Timing = this.modelActions['backrest_adjustment45']?.time > 0
      const br65Timing = this.modelActions['backrest_adjustment65']?.time > 0

      if (this.modelActions['mobilization_position']?.time > 0) {
        await this.playAnimation('mobilization_position', true)
      }
      if (this.modelActions['mobilization_from_min']?.time > 0) {
        await this.playAnimation('mobilization_from_min', true)
      }

      if (trendelenburgTiming) {
        await this.playAnimation('trendelenburg', true)
        this.playAnimation('reverse_trendelenburg', false)
      } else if (reverseTrendelenburgTiming) {
        //
      } else if (bhdTiming) {
        this.modelActions['reverse_trendelenburg'].setEffectiveWeight(0)
        this.playAnimation('reverse_trendelenburg', false, 100)
        this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(0)
        this.playAnimation('bed_height_adjustment_down', true, 100)

        this.modelActions['min_to_reversetrendelenburg'].setEffectiveWeight(1)
       this.playAnimation('min_to_reversetrendelenburg', false)
      } else {
        this.playAnimation('reverse_trendelenburg', false)
      }

      this.playAnimation('thighrest_adjustment', false)

      if (br45Timing) {
        // 
      } else if (br30Timing) {
        this.handleBackrestAdjustment('up')
      } else {
        await this.handleBackrestAdjustment('up')
        this.handleBackrestAdjustment('up')
      }

      this.resetModel()
    },

    async handleLegAdjustment(animation, direction) {
      if (this.modelActions['mobilization_position']?.time > 0) {
        await this.playAnimation('mobilization_position', true, 5)
      }

      if (this.modelActions['vascular_position']?.time > 0) {
        await this.playAnimation('vascular_position', true, 5)
      }

      if (this.modelActions['cardiac_chair']?.time > 0 || this.modelActions['cardiac_chair_from_min']?.time > 0) {
        return
      }

      this.resetModel()

      const cr1Timing = this.modelActions['calfrest_adjustment']?.time > 0
      const cr2Timing = this.modelActions['calfrest_adjustment2']?.time > 0

      if (animation === 'calfrest_adjustment') {
        if (direction === 'up') {
          if (cr1Timing) {
            this.modelActions['calfrest_adjustment2_rev'].setEffectiveWeight(0)
            this.modelActions['calfrest_adjustment2'].setEffectiveWeight(1)
            this.modelActions['calfrest_adjustment'].setEffectiveWeight(0)
            this.playAnimation('calfrest_adjustment2_rev', true, 100)
            await this.playAnimation('calfrest_adjustment2', false)
          } else {
            this.modelActions['calfrest_adjustment_rev'].setEffectiveWeight(0)
            this.modelActions['calfrest_adjustment'].setEffectiveWeight(1)
            this.modelActions['thighrest_adjustment'].setEffectiveWeight(0)
            this.playAnimation('calfrest_adjustment_rev', true, 100)
            await this.playAnimation('calfrest_adjustment', false)
          }
        } else {
          if (cr2Timing) {
            this.modelActions['calfrest_adjustment2_rev'].setEffectiveWeight(1)
            this.modelActions['calfrest_adjustment2'].setEffectiveWeight(0)
            this.playAnimation('calfrest_adjustment2', true, 100)
            await this.playAnimation('calfrest_adjustment2_rev', false)
          } else if (cr1Timing) {
            this.modelActions['calfrest_adjustment2_rev'].setEffectiveWeight(0)
            this.modelActions['calfrest_adjustment'].setEffectiveWeight(0)
            this.modelActions['calfrest_adjustment_rev'].setEffectiveWeight(1)
            this.playAnimation('calfrest_adjustment2_rev', true, 100)
            this.playAnimation('calfrest_adjustment', true, 100)
            await this.playAnimation('calfrest_adjustment_rev', false)

            this.modelActions['thighrest_adjustment'].setEffectiveWeight(1)
          }
        }
      } else {
        if (direction === 'up') {
          this.modelActions['thighrest_adjustment_rev'].setEffectiveWeight(0)
          this.modelActions['thighrest_adjustment'].setEffectiveWeight(1)
          this.playAnimation('thighrest_adjustment_rev', true, 100)
          await this.playAnimation('thighrest_adjustment', false)
        } else {

          if (cr2Timing) {
            this.modelActions['calfrest_adjustment2_rev'].setEffectiveWeight(1)
            this.modelActions['calfrest_adjustment2'].setEffectiveWeight(0)
            this.playAnimation('calfrest_adjustment2', true, 100)
            await this.playAnimation('calfrest_adjustment2_rev', false)
          }
          if (cr1Timing) {
            this.modelActions['calfrest_adjustment2_rev'].setEffectiveWeight(0)
            this.modelActions['calfrest_adjustment'].setEffectiveWeight(0)
            this.modelActions['calfrest_adjustment_rev'].setEffectiveWeight(1)
            this.playAnimation('calfrest_adjustment2_rev', true, 100)
            this.playAnimation('calfrest_adjustment', true, 100)
            await this.playAnimation('calfrest_adjustment_rev', false)

            this.modelActions['thighrest_adjustment'].setEffectiveWeight(1)
          }

          this.modelActions['calfrest_adjustment_rev'].setEffectiveWeight(0)
          this.modelActions['thighrest_adjustment'].setEffectiveWeight(0)
          this.modelActions['thighrest_adjustment_rev'].setEffectiveWeight(1)
          this.playAnimation('thighrest_adjustment', true, 100)
          this.playAnimation('calfrest_adjustment_rev', true, 100)
          await this.playAnimation('thighrest_adjustment_rev', false)
          this.modelActions['thighrest_adjustment_rev'].setEffectiveWeight(0)
        }
      }
    },

    async handleFootSiderails(direction) {

      if (this.modelActions['mobilization_position']?.time > 0) {
        await this.playAnimation('mobilization_position', true, 5)
      }

      const fbltl15Timing = this.modelActions['frame_based_lateral_tilt_left15']?.time > 0
      const fbltl30Timing = this.modelActions['frame_based_lateral_tilt_left30']?.time > 0
      const fbltr15Timing = this.modelActions['frame_based_lateral_tilt_right15']?.time > 0
      const fbltr30Timing = this.modelActions['frame_based_lateral_tilt_right30']?.time > 0

      // dont fold down when in 30degree
      if ((fbltl30Timing || fbltr30Timing) && direction !== 'down') return

      if ((fbltl15Timing || fbltr15Timing) && direction !== 'down') {
        if (this.transferModeOn) {
          this.playAnimation('folding_of_foot_siderails', direction === 'down')
        } else {
          this.$store.commit('navigation/setDisableBoard', true)
          return
        }
      }

      this.playAnimation('folding_of_foot_siderails', direction === 'down')
    },

    async handleHeadSiderails(direction) {

      if (this.modelActions['mobilization_position']?.time > 0) {
        await this.playAnimation('mobilization_position', true, 5)
      }

      const fbltl15Timing = this.modelActions['frame_based_lateral_tilt_left15']?.time > 0
      const fbltl30Timing = this.modelActions['frame_based_lateral_tilt_left30']?.time > 0
      const fbltr15Timing = this.modelActions['frame_based_lateral_tilt_right15']?.time > 0
      const fbltr30Timing = this.modelActions['frame_based_lateral_tilt_right30']?.time > 0

      // dont fold down when in 30degree
      if ((fbltl30Timing || fbltr30Timing) && direction !== 'down') return

      if ((fbltl15Timing || fbltr15Timing) && direction !== 'down') {
        if (this.transferModeOn) {
          this.playAnimation('folding_of_head_siderails', direction === 'down')
        } else {
          this.$store.commit('navigation/setDisableBoard', true)
          return
        }
      }

      this.playAnimation('folding_of_head_siderails', direction === 'down')
    },

    async handleBackrestAdjustment(direction) {
      if (this.modelActions['mobilization_position']?.time > 0) {
        await this.playAnimation('mobilization_position', true, 5)
      }

      if (this.modelActions['cardiac_chair']?.time > 0 || this.modelActions['cardiac_chair_from_min']?.time > 0) {
        return
      }

      this.resetModel()

      const br30Timing = this.modelActions['backrest_adjustment30']?.time > 0
      const br45Timing = this.modelActions['backrest_adjustment45']?.time > 0
      const br70Timing = this.modelActions['backrest_adjustment70']?.time > 0

      if (direction === 'up') {
        if (br70Timing) {
          return
        } else if(br45Timing) {
          this.modelActions['backrest_adjustment70rev'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment70'].setEffectiveWeight(1)
          this.modelActions['backrest_adjustment45'].setEffectiveWeight(0)
          this.playAnimation('backrest_adjustment70rev', true, 100)
          await this.playAnimation('backrest_adjustment70', false)
        } else if(br30Timing) {
          this.modelActions['backrest_adjustment45rev'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment45'].setEffectiveWeight(1)
          this.modelActions['backrest_adjustment30'].setEffectiveWeight(0)
          this.playAnimation('backrest_adjustment45rev', true, 100)
          await this.playAnimation('backrest_adjustment45', false)
        } else {
          this.modelActions['backrest_adjustment30rev'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment30'].setEffectiveWeight(1)
          this.playAnimation('backrest_adjustment30rev', true, 100)
          await this.playAnimation('backrest_adjustment30', false)
        }
      } else {
        
        if (br70Timing) {
          this.modelActions['backrest_adjustment70'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment70rev'].setEffectiveWeight(1)
          this.playAnimation('backrest_adjustment70', true, 100)
          await this.playAnimation('backrest_adjustment70rev', false)
        } else if(br45Timing) {
          this.modelActions['backrest_adjustment70rev'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment45rev'].setEffectiveWeight(1)
          this.modelActions['backrest_adjustment45'].setEffectiveWeight(0)
          this.playAnimation('backrest_adjustment70rev', true, 100)
          this.playAnimation('backrest_adjustment45', true, 100)
          await this.playAnimation('backrest_adjustment45rev', false)
        } else if(br30Timing) {
          this.modelActions['backrest_adjustment45rev'].setEffectiveWeight(0)
          this.modelActions['backrest_adjustment30rev'].setEffectiveWeight(1)
          this.modelActions['backrest_adjustment30'].setEffectiveWeight(0)
          this.playAnimation('backrest_adjustment30', true, 100)
          this.playAnimation('backrest_adjustment45rev', true, 100)
          await this.playAnimation('backrest_adjustment30rev', false)
        } else {
          return
        }
      }
    },

    async handleLateralTilting(name) {
      if (this.modelActions['mobilization_position']?.time > 0) {
        await this.playAnimation('mobilization_position', true, 5)
      }

      this.resetModel()

      const fbltl15Timing = this.modelActions['frame_based_lateral_tilt_left15']?.time > 0
      const fbltl30Timing = this.modelActions['frame_based_lateral_tilt_left30']?.time > 0
      const fbltr15Timing = this.modelActions['frame_based_lateral_tilt_right15']?.time > 0
      const fbltr30Timing = this.modelActions['frame_based_lateral_tilt_right30']?.time > 0

      const revertAnimations = [
        'bed_height_adjustment_up',
        'bed_height_adjustment_down',
        'trendelenburg',
        'reverse_trendelenburg',
        'trendelenburg_z_minima',
        'emergency_trendelenburg',
        'combined_tilting',
        'reverse_combined_tilting',
        'cardiac_chair',
        'cardiac_chair_from_min',
        'mobilization_position',
        'mobilization_from_min',
      ]

      if (name === 'frame_based_lateral_tilt_left') {
        if (fbltr30Timing) {
          await this.resetAnimations(revertAnimations, true)
          this.resetAnimations(['frame_based_lateral_tilt_right30'], true)
        } else if(fbltr15Timing) {
          await this.resetAnimations(revertAnimations, true)
          this.resetAnimations(['frame_based_lateral_tilt_right15'], true)
        } else if(fbltl15Timing) {
          await this.resetAnimations(revertAnimations, true)
          this.playAnimation('frame_based_lateral_tilt_left30', false)
          this.playAnimation('folding_of_foot_siderails', true)
          this.playAnimation('folding_of_head_siderails', true)
        } else if(fbltl30Timing) {
          return
        } else {
          if (this.siderailsCheck) {
            if (this.transferModeOn) {
              await this.resetAnimations(revertAnimations, true)
              this.playAnimation('frame_based_lateral_tilt_left15', false)
            } else {
              this.$store.commit('navigation/setDisableBoard', true)
              return
            }
            return
          } else {
            await this.resetAnimations(revertAnimations, true)
            this.playAnimation('frame_based_lateral_tilt_left15', false)
          }
        }
      } else {
        if (fbltl30Timing) {
          await this.resetAnimations(revertAnimations, true)
          this.resetAnimations(['frame_based_lateral_tilt_left30'], true)
        } else if(fbltl15Timing) {
          await this.resetAnimations(revertAnimations, true)
          this.resetAnimations(['frame_based_lateral_tilt_left15'], true)
        } else if(fbltr15Timing) {
          await this.resetAnimations(revertAnimations, true)
          this.playAnimation(['frame_based_lateral_tilt_right30'], false)
          this.playAnimation('folding_of_foot_siderails', true)
          this.playAnimation('folding_of_head_siderails', true)
        } else if(fbltr30Timing) {
          return
        } else {
          if (this.siderailsCheck) {
            if (this.transferModeOn) {
              this.playAnimation('frame_based_lateral_tilt_right15', false)
            } else {
              await this.resetAnimations(revertAnimations, true)
              this.$store.commit('navigation/setDisableBoard', true)
              return
            }
            return
          } else {
            await this.resetAnimations(revertAnimations, true)
            this.playAnimation('frame_based_lateral_tilt_right15', false)
          }
        }
      }
    },

    async resetModel() {
      this.modelActions['trendelenburg_z_minima_do_maxima'].setEffectiveWeight(0)
      this.modelActions['rev_trendelengurg_z_maxima'].setEffectiveWeight(0)
      this.modelActions['trendelenburg_z_minima'].setEffectiveWeight(0)
      this.modelActions['rev_trendelenburg_do_min'].setEffectiveWeight(0)
      this.modelActions['rev_trendelengurg_z_maxima_backward'].setEffectiveWeight(0)
      this.modelActions['trendelenburg_z_minima_do_maxima_backward'].setEffectiveWeight(0)
      this.modelActions['trendelenburg_z_minima_reversed'].setEffectiveWeight(0)
      this.modelActions['rev_trendelenburg_do_min_backward'].setEffectiveWeight(0)
      this.modelActions['mobilization_to_min'].setEffectiveWeight(0)
      this.modelActions['cardiac_chair_from_min'].setEffectiveWeight(0)

      this.modelActions['trendelenburg'].setEffectiveWeight(1)
      this.modelActions['reverse_trendelenburg'].setEffectiveWeight(1)
      this.modelActions['bed_height_adjustment_up'].setEffectiveWeight(1)
      this.modelActions['bed_height_adjustment_down'].setEffectiveWeight(1)
      this.modelActions['mobilization_position'].setEffectiveWeight(1)
      this.modelActions['cardiac_chair'].setEffectiveWeight(1)

      await this.playAnimation('trendelenburg_z_minima_do_maxima', true, 100)
      await this.playAnimation('trendelenburg_z_minima', true, 100)
      await this.playAnimation('rev_trendelengurg_z_maxima', true, 100)
      await this.playAnimation('rev_trendelenburg_do_min', true, 100)
      await this.playAnimation('rev_trendelengurg_z_maxima_backward', true, 100)
      await this.playAnimation('trendelenburg_z_minima_do_maxima_backward', true, 100)
      await this.playAnimation('trendelenburg_z_minima_reversed', true, 100)
      await this.playAnimation('rev_trendelenburg_do_min_backward', true, 100)
      await this.playAnimation('mobilization_to_min', true, 100)
      await this.playAnimation('cardiac_chair_from_min', true, 100)
    },

		async handleAnimationRequest(animationName, reverse) {
			this.hideInteractionPrompt()

      if (animationName === 'vascular_position') {
        if (this.modelActions['calfrest_adjustment2']?.time > 0) {
          return
        }
      }
      
      if (this.$store.state.activeProduct === 'multicare') {
        await this.resetModel()
      }

			let animationConfig = this.animationsList[animationName]
      let isOppositeAnimationActive = this.modelActions[animationConfig.opposite]?.time > 0

      if (animationConfig.conditional?.length) {

        for (const anim of animationConfig.conditional) {
          const isConditionalAnimationActive = this.modelActions[anim.conditionalAnimation]?.time > 0

          if (isConditionalAnimationActive) {
            animationName = anim.substituteAnimation
            animationConfig = this.animationsList[animationName]
            isOppositeAnimationActive = false
          }
        }

        if (animationConfig.overrides?.length) {
          this.modelActions[animationName].setEffectiveWeight(1)

          for (const anim of animationConfig.overrides) {
            this.modelActions[anim.animation].setEffectiveWeight(0)
            if (this.$store.state.activeProduct === 'multicare') {
              this.playAnimation(anim.animation, anim.reverse, 100)
            } else {
              await this.playAnimation(anim.animation, anim.reverse, 100)
            }
          }
        }
      }

			if (isOppositeAnimationActive) {
				console.log('Reversing opposite animation:', animationConfig.opposite || null)
				await this.resetAnimations([animationConfig.opposite])
			} else {

        // reverse in order
        if ('reverseInOrder' in animationConfig) {
          for (const anim of animationConfig.reverseInOrder) {
            if (anim === 'backrest_adjustment') {
              this.reverseBackrestAdjustment()
            } else if (anim === 'leg_adjustment') {
              if (animationName === 'vascular_position' || animationName === 'mobilization_position' || animationName === 'mobilization_from_min') {
                await this.reverseLegAdjustment()
              } else {
                this.reverseLegAdjustment()
              }
            } else {
              await this.playAnimation(anim, true, 5)
            }
          }
        }

				await this.resetAnimations(animationConfig.conflicting, 5)
				console.log('Conflicting animations reversed')

        if (reverse && 'reversable' in animationConfig) {
          if (animationConfig.reversable) {
            this.playAnimation(animationName, reverse)
          }
        } else {

          if (animationName === 'mobilization_position') {
            if (this.modelActions['backrest_adjustment30rev']) {
              this.modelActions['backrest_adjustment30rev'].setEffectiveWeight(0)
              await this.playAnimation('backrest_adjustment30rev', true, 100)
            }
          }

          this.playAnimation(animationName, reverse)
        }
			}
		},

		playAnimation(name, reverse, speed = 1) {
			return new Promise(resolve => {
				console.log(`${reverse ? 'Reversing' : 'Playing'} animation:`, name)
				const action = this.modelActions[name]
				const timeScaleSign = reverse ? -1 : 1

				// start the rendering loop
				this.modelViewer.play()

				action.setEffectiveTimeScale(speed * timeScaleSign)
				action.enabled = true
				action.paused = false
				action.play()

				this.animationsInProgress = this.animationsInProgress + 1

				// set up listener when animation finishes
				// assigning the callback to action.finishedCallback makes the listener unique so that it can be removed afterwards
				this.modelViewer.animationMixer.addEventListener('finished', action.finishedCallback = e => {
					const finishedTrackName = e.action.getClip().name

					if (finishedTrackName === this.animationsList[name].track) {
						this.modelViewer.animationMixer.removeEventListener('finished', action.finishedCallback)

						// save resources by pausing the rendering loop when no animations are playing
						if (this.animationsInProgress === 1) {
							this.modelViewer.pause()
						}
						this.animationsInProgress = this.animationsInProgress - 1

						action.enabled = !reverse

						resolve()
					}
				})
			})
		},

    async reverseBackrestAdjustment() {

      const level3Angle = this.$store.state.activeProduct === 'multicare' ? 'backrest_adjustment70' : 'backrest_adjustment65'
      const level3RevAngle = this.$store.state.activeProduct === 'multicare' ? 'backrest_adjustment70rev' : 'backrest_adjustment65rev'

      const br1Timing = this.modelActions['backrest_adjustment30']?.time > 0
      const br2Timing = this.modelActions['backrest_adjustment45']?.time > 0
      const br3Timing = this.modelActions[level3Angle]?.time > 0

      if (br3Timing) {
        this.modelActions[level3Angle].setEffectiveWeight(0)
        this.modelActions[level3RevAngle].setEffectiveWeight(1)
        this.playAnimation(level3Angle, true, 100)
        await this.playAnimation(level3RevAngle, false, 3)
      } 
      
      if(br2Timing) {
        this.modelActions[level3RevAngle].setEffectiveWeight(0)
        this.modelActions['backrest_adjustment45rev'].setEffectiveWeight(1)
        this.modelActions['backrest_adjustment45'].setEffectiveWeight(0)
        this.playAnimation(level3RevAngle, true, 100)
        this.playAnimation('backrest_adjustment45', true, 100)
        await this.playAnimation('backrest_adjustment45rev', false, 3)
      } 
      if(br1Timing) {
        this.modelActions['backrest_adjustment45rev'].setEffectiveWeight(0)
        this.modelActions['backrest_adjustment30rev'].setEffectiveWeight(1)
        this.modelActions['backrest_adjustment30'].setEffectiveWeight(0)
        this.playAnimation('backrest_adjustment30', true, 100)
        this.playAnimation('backrest_adjustment45rev', true, 100)
        await this.playAnimation('backrest_adjustment30rev', false, 3)
        this.modelActions['backrest_adjustment30rev'].setEffectiveWeight(0)
      }
    },

    async reverseLegAdjustment() {
      const cr1Timing = this.modelActions['calfrest_adjustment']?.time > 0
      const cr2Timing = this.modelActions['calfrest_adjustment2']?.time > 0
      const tTiming = this.modelActions['thighrest_adjustment']?.time > 0
      const vascularTiming = this.modelActions['vascular_position']?.time > 0

      if (vascularTiming) {
        this.modelActions['vascular_position'].setEffectiveWeight(1)
        this.playAnimation('vascular_position', true)
      } else {
        if (cr2Timing) {
          this.modelActions['calfrest_adjustment2_rev'].setEffectiveWeight(1)
          this.modelActions['calfrest_adjustment2'].setEffectiveWeight(0)
          this.playAnimation('calfrest_adjustment2', true, 100)
          await this.playAnimation('calfrest_adjustment2_rev', false)
        }
        if (cr1Timing) {
          this.modelActions['calfrest_adjustment2_rev'].setEffectiveWeight(0)
          this.modelActions['calfrest_adjustment'].setEffectiveWeight(0)
          this.modelActions['calfrest_adjustment_rev'].setEffectiveWeight(1)
          this.playAnimation('calfrest_adjustment2_rev', true, 100)
          this.playAnimation('calfrest_adjustment', true, 100)
          await this.playAnimation('calfrest_adjustment_rev', false)

          this.modelActions['thighrest_adjustment'].setEffectiveWeight(1)
        }

        if (tTiming) {
          this.modelActions['calfrest_adjustment_rev'].setEffectiveWeight(0)
          this.modelActions['thighrest_adjustment'].setEffectiveWeight(0)
          this.modelActions['thighrest_adjustment_rev'].setEffectiveWeight(1)
          this.playAnimation('thighrest_adjustment', true, 100)
          this.playAnimation('calfrest_adjustment_rev', true, 100)
          await this.playAnimation('thighrest_adjustment_rev', false)
          this.modelActions['thighrest_adjustment_rev'].setEffectiveWeight(0)
        }
      }      
    },

		resetAnimations(animationNames = [], speed = 1) {
			const animationsPromises = []

			animationNames.forEach(animationName => {
				if (this.modelActions[animationName]?.time > 0) {
					animationsPromises.push(this.playAnimation(animationName, true, speed))
				}
			})

			return Promise.all(animationsPromises)
		},

		setModelNodeVisibility(nodeName, visible) {
      console.log(nodeName)
      console.log(this.modelViewer.model.primitives)
			const node = this.modelViewer.model.primitives.find(it => it.name === nodeName)
			node.setMeshVisibility(visible)
		},

		updateProgress(e) {
			this.$store.commit('setModelLoadingProgress', e.detail.totalProgress)
		},

		hideInteractionPrompt() {
			this.modelPrompt = 'none'
		},

		setCameraPosition(position) {
			this.modelViewer.cameraOrbit = position.orbit
			this.modelViewer.cameraTarget = position.target
		},

		resetCameraPosition() {
			this.setCameraPosition({
				orbit: '-50deg 75deg 65%',
				target: 'auto auto auto'
			})
		},

		onHotspotClick(hotspot) {
			const explanations = hotspot.dataset.explanations?.split('.')
			if (explanations) {
				const explanation = explanations[0]
				const subExplanation = explanations[1] || null
        const explanationItem = this.productConfig.explanations[explanation]
        const subExplanationItem = this.productConfig.explanations[subExplanation]
				this.$store.commit('navigation/setActiveSecondary', 'key_features.' + explanation)
				this.$store.commit('setExplanationPaneContent', explanationItem)
				this.$store.commit('setExplanationPaneSub', subExplanation)
				this.$store.commit('setExplanationPaneOpen', true)

        if (subExplanationItem?.camera) {
          this.setCameraPosition(subExplanationItem.camera)
        }
			}
		},

		onCameraChange() {
			this.cameraOrbit = this.modelViewer?.getCameraOrbit() || ''
			this.cameraTarget = this.modelViewer?.getCameraTarget() || ''
		}
	}
}
</script>

<style lang="scss" scoped>
model-viewer {
	width: 100%;
	height: 100%;
	--poster-color: transparent;
	--min-hotspot-opacity: 0;

	&::part(default-progress-bar) {
		top: 30px;
		background-color: $clrRed;
	}
}

::v-deep(.Hotspot) {
	display: none;
	position: relative;
	width: 26px;
	height: 26px;
	background-color: transparentize($color: $clrWhite, $amount: 0.2);
	border-radius: 100px;
	box-shadow: 0 0 0 4px transparentize($color: $clrWhite, $amount: 0.5);
	transition: all 300ms ease 200ms;
	&.active,
	&:hover,
	&:disabled,
	&:not([data-visible]) {
		background-color: transparent;
		box-shadow: 0 0 0 0 transparent;
		transition: all 300ms ease;
	}
	&:disabled {
		pointer-events: none;
	}
	.hotspots-visible & {
		display: block;
	}

	&::before {
		content: "";
		position: absolute;
		width: 17px;
		height: 17px;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		border-radius: 100px;
		border: 1px solid $clrWhite;
		background-color: $clrRed;
		background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M128 29.799v196.403M226.202 128H29.799' stroke-linecap='round' stroke-miterlimit='10' stroke-width='51.7px' fill='none' stroke='%23FFFFFF'/%3E%3C/svg%3E%0A");
		background-size: 10px;
		background-repeat: no-repeat;
		background-position: center;
		transition: all 300ms ease 200ms;
	}
	&.active::before,
	&:hover::before,
	&:disabled::before,
	&:not([data-visible])::before {
		transform: translate(-50%, -50%) scale(0.6);
		opacity: 0;
		transition: all 300ms ease;
	}

	.HotspotAnnotation {
		position: absolute;
		left: 50%;
		bottom: 24px;
		padding: 0.2em 0.75em;
		transform: translate(-50%, 30%);
		opacity: 0;
		font-size: 12px;
		font-weight: 500;
		white-space: nowrap;
		border-radius: 100px;
		background-color: $clrWhite;
		transition: all 300ms ease;
		pointer-events: none;
		z-index: 100;

		&::before {
			content: "";
			position: absolute;
			width: 0;
			height: 0;
			bottom: -6px;
			left: calc(50% - 6px);
			border-style: solid;
			border-width: 6px 6px 0 6px;
			border-color: #ffffff transparent transparent transparent;
		}
	}
	&:hover, &.active {
		.HotspotAnnotation {
			opacity: 1;
			transform: translate(-50%, 0);
			pointer-events: all;
			color: $clrRed;
		}
	}
}

.debug-info {
	position: absolute;
	top: 50px;
	left: 300px;
	font-size: 10px;
}
</style>
